import * as iOSBridge from "iOSBridge";
import readlangClient from "readlangClient";
import stores from "stores/stores";

/**
 * The languages Google can limit search results to, by their codes (after
 * `lang_`), which match Readlang's apart from Chinese
 */
const googleResultLanguages =
  // cspell: disable-next-line
  "af ar be bg ca cs da de el en eo es et fa fi fr hi hr hu hy id is it iw ja ko lt lv nl no pl pt ro ru sk sl sr sv sw th tl tr uk vi zh-CN".split(
    " "
  );

/** Google's parameter limiting results to pages in `language`, if it can */
const resultLanguageParameter = (language: string) => {
  const resultLanguage = language === "zh" ? "zh-CN" : language;
  return googleResultLanguages.includes(resultLanguage)
    ? `lr=lang_${resultLanguage}`
    : null;
};

/** A Google search URL for pages in `language`, needing only the query appended */
export const searchURLPrefix = (language: string) => {
  const parameter = resultLanguageParameter(language);
  return `https://www.google.com/search?${parameter ? `${parameter}&` : ""}q=`;
};

/** Google's home page, whose searches find pages in `language` */
export const searchHomeURL = (language: string) => {
  const parameter = resultLanguageParameter(language);
  return `https://www.google.com/${parameter ? `?${parameter}` : ""}`;
};

export const searchURL = (language: string, query: string) =>
  searchURLPrefix(language) + encodeURIComponent(query);

export interface PopularDomain {
  _id: string;
  domainName: string;
  path: string;
  title?: string;
  description?: string;
}

export const fetchPopularDomains = async (language: string) =>
  (
    await readlangClient.request<PopularDomain[]>(
      "GET",
      "/api/popularDomains",
      { language, limit: 50 }
    )
  ).data;

export const popularDomainURL = (domain: PopularDomain) =>
  "http://" + domain.domainName + domain.path;

/**
 * In the iOS app, opens a link to a website in the app's browser, which has
 * the Web Reader built in, in place of following it: whether it did
 */
export const openInAppBrowser = (event: MouseEvent) => {
  const bridge = iOSBridge.get();
  if (bridge) {
    event.preventDefault();
    bridge.openBrowser((event.currentTarget as HTMLAnchorElement).href);
  }
  return Boolean(bridge);
};

/**
 * What the iOS app's browser offers as the user types in its address bar:
 * searches and popular sites in the language they're learning
 */
export const browserStart = async () => {
  const language = stores.user.currentLearningLanguage.value;
  return {
    searchURLPrefix: searchURLPrefix(language),
    sites: (await fetchPopularDomains(language)).map((domain) => ({
      title: domain.title?.trim() || domain.domainName,
      url: popularDomainURL(domain),
    })),
  };
};
